// SettingSubsidiaries.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ReactFlow,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    StepEdge,
    Background,
    Controls,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import DataTable from "react-data-table-component";

import { Box, Button, Typography, Paper, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

import axios from "axios";
import qs from "qs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";

import { toPng } from "html-to-image";

import CompanyCreatePopup from "./CompanyCreate";
import CompanyUpdatePopup from "./CompanyUpdate";

import PageHeader from "../../../components/PageHeader";

const edgeTypes = { step: StepEdge };

const API_LIST = "/client/company";
const API_DELETE = "/client/company/delete";

// ✅ graph APIs
const API_GRAPH_READ = "/company-graph/read";
const API_GRAPH_SAVE = "/company-graph/save";

function getParentId(row) {
    return (
        row?.parent_company_uuid ||
        row?.company_parent_uuid ||
        row?.parent_uuid ||
        row?.holding_company_uuid ||
        row?.company_parent ||
        row?.parent_id ||
        null
    );
}

function toPct(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function layoutNodesSimple(companies, parentId) {
    const cols = 3;
    const cellW = 260;
    const cellH = 120;
    const startX = 0;
    const startY = 160;

    const positions = new Map();
    const children = parentId ? companies.filter((c) => c.company_uuid !== parentId) : companies;

    children.forEach((c, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        positions.set(c.company_uuid, {
            x: startX + col * cellW,
            y: startY + row * cellH,
        });
    });

    return positions;
}

/**
 * ✅ Always returns a non-empty graph:
 * - Always includes a parent node (id = parentMeta.id, or fallback "PARENT")
 * - If parent id is missing, edges will connect from "PARENT" to each child
 */
function buildGraphFromCompanies(companies, parentMeta) {
    const rawParentId = parentMeta?.id || null;
    const parentId = rawParentId || "PARENT";
    const parentName = parentMeta?.name || "Parent Company";

    const list = Array.isArray(companies) ? companies : [];
    const positions = layoutNodesSimple(list, rawParentId);

    const parentNode = [
        {
            id: parentId,
            position: { x: 0, y: 0 },
            data: { label: parentName, __isParent: true },
            style: {
                border: "2px solid #1976d2",
                borderRadius: 14,
                padding: 8,
                background: "rgba(25,118,210,0.08)",
                fontWeight: 700,
                minWidth: 220,
            },
        },
    ];

    const children = rawParentId ? list.filter((c) => c.company_uuid !== rawParentId) : list;

    const childNodes = children.map((c) => {
        const pct = toPct(c.company_shareholding ?? c.percentage);
        const name = c.company_name ?? c.name ?? "";
        return {
            id: c.company_uuid,
            position: positions.get(c.company_uuid) || { x: 0, y: 0 },
            data: {
                ...c,
                name,
                percentage: pct,
                label: `${name} (${pct}%)`,
            },
        };
    });

    const idSet = new Set([parentId, ...children.map((c) => c.company_uuid)]);

    const edges = [];
    for (const c of children) {
        const src = getParentId(c);

        if (src && idSet.has(src)) {
            edges.push({
                id: `${src}-${c.company_uuid}`,
                source: src,
                target: c.company_uuid,
                type: "step",
            });
        } else {
            edges.push({
                id: `${parentId}-${c.company_uuid}`,
                source: parentId,
                target: c.company_uuid,
                type: "step",
            });
        }
    }

    return { nodes: [...parentNode, ...childNodes], edges };
}

export default function SettingSubsidiaries() {
    const { t } = useTranslation();

    const account = useSelector((state) => state.account?.value);

    const parentCompanyId =
        account?.client_id || account?.company_uuid || account?.company_id || account?.id || null;

    const parentCompanyName =
        account?.client_name || account?.company_name || account?.name || "Parent Company";

    const parentMeta = useMemo(
        () => ({ id: parentCompanyId, name: parentCompanyName }),
        [parentCompanyId, parentCompanyName]
    );

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    // graph/table data
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [tableData, setTableData] = useState([]);

    // ✅ viewport state (saved)
    const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

    // create popup state
    const [createOpen, setCreateOpen] = useState(false);

    // update popup state
    const [updateOpen, setUpdateOpen] = useState(false);
    const [updateRow, setUpdateRow] = useState(null);

    // table filter
    const [tableFilter, setTableFilter] = useState("");

    // export ref
    const reactFlowWrapRef = useRef(null);

    // ✅ reactflow instance
    const rfInstanceRef = useRef(null);

    // ✅ debounce timer ref for saving
    const saveTimerRef = useRef(null);

    /**
     * ✅ IMPORTANT:
     * - If graph read is missing/failed, do NOT save on initial mount/fitView.
     * - Enable save only after first real user interaction.
     */
    const saveEnabledRef = useRef(false);

    /**
     * ✅ Prevent onMoveEnd from saving when we move programmatically.
     */
    const programmaticMoveRef = useRef(false);

    /**
     * ✅ Track whether we loaded from saved graph (affects fitView behavior)
     */
    const hasSavedGraphRef = useRef(false);

    const apiPost = useCallback(async (path, data) => {
        const options = {
            withCredentials: true,
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            data: qs.stringify(data || {}),
            url: `${process.env.REACT_APP_BASE_SERVER}${path}`,
        };
        const res = await axios(options);
        return res?.data;
    }, []);

    // ✅ read saved graph json; returns null if not found
    const fetchSavedGraph = useCallback(async () => {
        if (!parentMeta?.id) return null;

        const res = await apiPost(API_GRAPH_READ, {
            parent_company_uuid: parentMeta.id,
        });

        if (!res?.ok || !res?.data) return null;

        return {
            viewport: res.data?.viewport || null,
            nodes: Array.isArray(res.data?.nodes) ? res.data.nodes : [],
            edges: Array.isArray(res.data?.edges) ? res.data.edges : [],
        };
    }, [apiPost, parentMeta?.id]);

    // ✅ save graph now (positions + viewport + edges)
    const saveGraphNow = useCallback(
        async ({ nextNodes, nextEdges, nextViewport } = {}) => {
            if (!parentMeta?.id) return;

            const payload = {
                parent_company_uuid: parentMeta.id,
                graph_json: JSON.stringify({
                    version: 1,
                    viewport: nextViewport || viewport,
                    nodes: (nextNodes || nodes).map((n) => ({
                        id: n.id,
                        position: n.position,
                    })),
                    edges: (nextEdges || edges).map((e) => ({
                        id: e.id,
                        source: e.source,
                        target: e.target,
                        type: e.type || "step",
                    })),
                }),
            };

            await apiPost(API_GRAPH_SAVE, payload);
        },
        [apiPost, edges, nodes, parentMeta?.id, viewport]
    );

    // ✅ debounced save wrapper (guarded by saveEnabledRef)
    const saveGraphDebounced = useCallback(
        (args) => {
            if (!saveEnabledRef.current) return;

            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(() => {
                saveGraphNow(args).catch((e) => console.error("save graph failed", e));
            }, 900);
        },
        [saveGraphNow]
    );

    const applySavedViewportToInstance = useCallback((vp) => {
        const inst = rfInstanceRef.current;
        if (!inst || !vp) return;

        try {
            programmaticMoveRef.current = true;
            inst.setViewport(vp, { duration: 0 });
        } finally {
            setTimeout(() => {
                programmaticMoveRef.current = false;
            }, 50);
        }
    }, []);

    /**
     * ✅ IMPORTANT:
     * - /client/company ALWAYS defines default graph
     * - /company-graph/read may fail but MUST NOT wipe the graph
     * - if read is missing/failed => saveEnabledRef stays false until user touches something
     * - if read exists => enable saves immediately
     */
    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        setLoadError("");

        try {
            const res = await apiPost(API_LIST, {});
            if (!res?.success) {
                setLoadError(res?.message || "Failed to load companies.");
                setTableData([]);

                const fallbackGraph = buildGraphFromCompanies([], parentMeta);
                hasSavedGraphRef.current = false;
                setNodes(fallbackGraph.nodes);
                setEdges(fallbackGraph.edges);
                setViewport({ x: 0, y: 0, zoom: 1 });

                saveEnabledRef.current = false;
                return;
            }

            const record = Array.isArray(res.data) ? res.data : [];
            setTableData(record);

            const defaultGraph = buildGraphFromCompanies(record, parentMeta);

            let saved = null;
            try {
                saved = await fetchSavedGraph();
            } catch (e) {
                console.warn("graph read failed, using default layout:", e);
                saved = null;
            }

            if (!saved) {
                hasSavedGraphRef.current = false;

                setNodes(defaultGraph.nodes);
                setEdges(defaultGraph.edges);
                setViewport({ x: 0, y: 0, zoom: 1 });

                // disable saving until user touches something
                saveEnabledRef.current = false;
                return;
            }

            // we have saved data
            hasSavedGraphRef.current = true;
            saveEnabledRef.current = true;

            const posMap = new Map((saved.nodes || []).map((n) => [n.id, n.position]));
            const mergedNodes = defaultGraph.nodes.map((n) => {
                const savedPos = posMap.get(n.id);
                return savedPos ? { ...n, position: savedPos } : n;
            });

            const mergedEdges =
                Array.isArray(saved.edges) && saved.edges.length > 0 ? saved.edges : defaultGraph.edges;

            setNodes(mergedNodes);
            setEdges(mergedEdges);

            if (saved.viewport) {
                setViewport(saved.viewport);
                // ✅ IMPORTANT: defaultViewport won't apply after mount, so set it on the instance
                requestAnimationFrame(() => applySavedViewportToInstance(saved.viewport));
            }
        } catch (e) {
            console.error(e);
            setLoadError("Failed to load companies.");

            const fallbackGraph = buildGraphFromCompanies([], parentMeta);
            hasSavedGraphRef.current = false;
            setNodes(fallbackGraph.nodes);
            setEdges(fallbackGraph.edges);
            setViewport({ x: 0, y: 0, zoom: 1 });

            saveEnabledRef.current = false;
        } finally {
            setLoading(false);
        }
    }, [apiPost, applySavedViewportToInstance, fetchSavedGraph, parentMeta]);

    useEffect(() => {
        fetchCompanies();

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [fetchCompanies]);

    // ✅ fitView only when NO saved graph exists
    useEffect(() => {
        const inst = rfInstanceRef.current;
        if (!inst) return;
        if (!nodes || nodes.length === 0) return;

        // if we loaded a saved graph, do NOT override it with fitView
        if (hasSavedGraphRef.current) return;

        const tmr = setTimeout(() => {
            try {
                programmaticMoveRef.current = true;
                inst.fitView({ padding: 0.2, duration: 200 });
            } catch (e) {
                // ignore
            } finally {
                setTimeout(() => {
                    programmaticMoveRef.current = false;
                }, 250);
            }
        }, 60);

        return () => clearTimeout(tmr);
    }, [nodes, edges]);

    // reactflow handlers
    const onNodesChange = useCallback((changes) => {
        setNodes((ns) => applyNodeChanges(changes, ns));
    }, []);

    const onEdgesChange = useCallback(
        (changes) => {
            // user interaction => enable saving
            saveEnabledRef.current = true;

            setEdges((es) => {
                const next = applyEdgeChanges(changes, es);
                saveGraphDebounced({ nextEdges: next });
                return next;
            });
        },
        [saveGraphDebounced]
    );

    const onConnect = useCallback(
        (params) => {
            // user interaction => enable saving
            saveEnabledRef.current = true;

            setEdges((es) => {
                const next = addEdge({ ...params, type: "step" }, es);
                saveGraphDebounced({ nextEdges: next });
                return next;
            });
        },
        [saveGraphDebounced]
    );

    // ✅ save positions only after drag stop
    const onNodeDragStop = useCallback(() => {
        saveEnabledRef.current = true;
        saveGraphDebounced({ nextNodes: nodes });
    }, [nodes, saveGraphDebounced]);

    // ✅ save viewport when pan/zoom ends (but ignore programmatic moves)
    const onMoveEnd = useCallback(
        (evt, nextViewport) => {
            setViewport(nextViewport);

            if (programmaticMoveRef.current) return;

            saveEnabledRef.current = true;
            saveGraphDebounced({ nextViewport });
        },
        [saveGraphDebounced]
    );

    const openUpdatePopup = useCallback(
        (nodeOrRow) => {
            const parentNodeId = parentMeta?.id || "PARENT";
            if (nodeOrRow?.id === parentNodeId) return;

            const row = nodeOrRow?.company_uuid
                ? nodeOrRow
                : tableData.find((r) => r.company_uuid === nodeOrRow?.id) || null;

            if (!row) return;

            setUpdateRow(row);
            setUpdateOpen(true);
        },
        [parentMeta?.id, tableData]
    );

    const onDeleteCall = useCallback(
        async (data) => {
            const res = await apiPost(API_DELETE, data);
            return !!res?.success;
        },
        [apiPost]
    );

    const handleDeleteRow = useCallback(
        async (row) => {
            if (!row?.company_uuid) return;

            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
            });

            if (!confirm.isConfirmed) return;

            try {
                setLoading(true);

                const ok = await onDeleteCall({ id: row.company_uuid });
                if (!ok) {
                    await Swal.fire({
                        title: "",
                        html: "<p>Cannot delete data, please try again later</p>",
                        showCancelButton: false,
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Ok",
                    });
                    return;
                }

                await Swal.fire({
                    title: "Deleted",
                    text: "Your data has been deleted.",
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: "Ok",
                });

                if (updateRow?.company_uuid === row.company_uuid) {
                    setUpdateOpen(false);
                    setUpdateRow(null);
                }

                await fetchCompanies();
            } finally {
                setLoading(false);
            }
        },
        [fetchCompanies, onDeleteCall, updateRow?.company_uuid]
    );

    const filteredRows = useMemo(() => {
        const q = (tableFilter || "").trim().toLowerCase();
        if (!q) return tableData;

        return tableData.filter((r) => {
            const name = r.company_name ?? r.name ?? "";
            return name.toLowerCase().includes(q) || (r.company_group || "").toLowerCase().includes(q);
        });
    }, [tableData, tableFilter]);

    const tableColumns = useMemo(() => {
        return [
            {
                name: t("menu.label622"),
                selector: (row) => row.company_name ?? row.name,
                sortable: true,
            },
            {
                name: t("menu.label621"),
                selector: (row) => row.company_group,
                sortable: true,
            },
            {
                name: t("company.label1"),
                selector: (row) => `${toPct(row.company_shareholding ?? row.percentage)}%`,
                sortable: true,
            },
            {
                name: t("emission.table.label6"),
                cell: (row) => (
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            size="small"
                            variant="contained"
                            color="info"
                            onClick={() => openUpdatePopup(row)}
                            sx={{ minWidth: 0, px: 1 }}
                            title={t("general.btnUpdate")}
                        >
                            <i className="ri-pencil-fill" />
                        </Button>

                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleDeleteRow(row)}
                            sx={{
                                minWidth: 0,
                                px: 1,
                                bgcolor: "#fbc02d",
                                color: "#111",
                                "&:hover": { bgcolor: "#f9a825" },
                            }}
                            title={t("general.btnDelete")}
                        >
                            <i className="ri-delete-bin-2-fill" />
                        </Button>
                    </div>
                ),
                ignoreRowClick: true,
                button: true,
                width: "130px",
            },
        ];
    }, [handleDeleteRow, openUpdatePopup, t]);

    const paginationComponentOptions = useMemo(
        () => ({
            rowsPerPageText: t("table.paginationResult"),
            rangeSeparatorText: t("table.paginationResultPage"),
        }),
        [t]
    );

    const graphNodes = useMemo(() => {
        return nodes.map((n) => {
            if (n.data?.__isParent) return n;
            const name = n.data?.name ?? n.data?.company_name ?? "";
            const pct = toPct(n.data?.percentage ?? n.data?.company_shareholding);
            return { ...n, data: { ...n.data, label: `${name} (${pct}%)` } };
        });
    }, [nodes]);

    const exportGraphPng = useCallback(async () => {
        try {
            const wrap = reactFlowWrapRef.current;
            if (!wrap) return;

            const viewportEl = wrap.querySelector(".react-flow__viewport");
            if (!viewportEl) return;

            const dataUrl = await toPng(viewportEl, {
                backgroundColor: "#ffffff",
                cacheBust: true,
            });

            const a = document.createElement("a");
            a.setAttribute("download", `subsidiaries-${Date.now()}.png`);
            a.setAttribute("href", dataUrl);
            a.click();
        } catch (e) {
            console.error(e);
            Swal.fire({
                title: "",
                html: "<p>Failed to export image</p>",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
        }
    }, []);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <PageHeader
                    title={t("menu.label73")}
                    labels={{
                        setting: "Settings",
                        subsidiaries: t("menu.label73"),
                    }}
                    actions={
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            color="primary"
                            onClick={() => setCreateOpen(true)}
                            disabled={loading}
                        >
                            {t("general.btnCreate")}
                        </Button>
                    }
                />

                {loadError ? (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                        {loadError}
                    </Alert>
                ) : null}

                {/* GRAPH */}
                <div className="row">
                    <div className="col-12">
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                            <Box sx={{ position: "relative", p: 2 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        position: "absolute",
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        maxWidth: "70%",
                                        textAlign: "center",
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                    title={parentMeta?.name || ""}
                                >
                                    {parentMeta?.name || "-"}
                                </Typography>

                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                    <Button startIcon={<DownloadIcon />} variant="outlined" onClick={exportGraphPng}>
                                        Export PNG
                                    </Button>
                                </Box>
                            </Box>

                            <Box
                                ref={reactFlowWrapRef}
                                sx={{ height: "28vh", bgcolor: "#fefefe", borderTop: "1px solid #eee" }}
                            >
                                <ReactFlow
                                    nodes={graphNodes}
                                    edges={edges}
                                    onInit={(instance) => {
                                        rfInstanceRef.current = instance;

                                        // ✅ DO NOT fitView here (it overrides saved viewport)
                                        // If we already have a saved viewport in state, apply it once the instance exists.
                                        if (hasSavedGraphRef.current && viewport) {
                                            requestAnimationFrame(() => applySavedViewportToInstance(viewport));
                                        }
                                    }}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    onConnect={onConnect}
                                    onNodeDragStop={onNodeDragStop}
                                    onMoveEnd={onMoveEnd}
                                    edgeTypes={edgeTypes}
                                    onNodeClick={(e, node) => openUpdatePopup(node)}
                                    defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                                >
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </Box>
                        </Paper>
                    </div>
                </div>

                {/* TABLE */}
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="col-lg-4" style={{ float: "right" }}>
                                    <div className="input-group">
                                        <span className="input-group-text" id="search">
                                            <i className="ri-search-line"></i>
                                        </span>
                                        <input
                                            value={tableFilter}
                                            onChange={(e) => setTableFilter(e.target.value)}
                                            type="text"
                                            className="form-control"
                                            placeholder={t("table.search")}
                                            aria-describedby="search"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <DataTable
                                    columns={tableColumns}
                                    data={filteredRows}
                                    noDataComponent={loading ? t("general.loading") || "Loading..." : t("table.empty")}
                                    pagination
                                    paginationComponentOptions={paginationComponentOptions}
                                    highlightOnHover
                                    pointerOnHover
                                    onRowClicked={(row) => openUpdatePopup(row)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* CREATE POPUP */}
                <CompanyCreatePopup
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    onSuccess={async () => {
                        setCreateOpen(false);
                        await fetchCompanies();
                    }}
                    title={`${t("general.btnCreate")} ${t("menu.label622")}`}
                    parentCompanyId={parentMeta?.id}
                />

                {/* UPDATE POPUP */}
                <CompanyUpdatePopup
                    open={updateOpen}
                    onClose={() => {
                        setUpdateOpen(false);
                        setUpdateRow(null);
                    }}
                    onSuccess={async () => {
                        setUpdateOpen(false);
                        setUpdateRow(null);
                        await fetchCompanies();
                    }}
                    title={`${t("general.btnUpdate")} ${t("menu.label622")}`}
                    parentCompanyId={parentMeta?.id}
                    company={updateRow}
                />
            </div>
        </div>
    );
}
